import { isSuccess, type IHTTPResponse } from '@yimoka/shared';

import {
  imServiceEntity,
} from '@/entities/im-service';
import type { ICourseEvalInviteActionInput, IMyStaffIMTokenReply } from '@/entities/im-service.d';

import { isRecord, readString } from './notice-payload';
import { createWorkspaceOpenIMClient } from './openim-client';
import {
  asArray,
  normalizeMessage,
  mergeMessages,
  normalizeConversations,
  normalizeMessages,
  readHistoryEnd,
} from './openim-normalizers';
import { WorkspaceIMStore } from './openim-store';
import type {
  IWorkspaceIMBridge,
  OpenIMEventHandler,
  RawOpenIMUserProfile,
  WorkspaceIMMessage,
  WorkspaceIMSnapshot,
  WorkspaceOpenIMClient,
  WorkspaceOpenIMConfig,
} from './openim-types';

const EVENT_NAMES = {
  connectFailed: 'OnConnectFailed',
  connectSuccess: 'OnConnectSuccess',
  connecting: 'OnConnecting',
  conversationChanged: 'OnConversationChanged',
  kickedOffline: 'OnKickedOffline',
  newConversation: 'OnNewConversation',
  receiveMessages: 'OnRecvNewMessages',
  syncFailed: 'OnSyncServerFailed',
  syncFinish: 'OnSyncServerFinish',
  syncStart: 'OnSyncServerStart',
  totalUnreadChanged: 'OnTotalUnreadMessageCountChanged',
  tokenExpired: 'OnUserTokenExpired',
  tokenInvalid: 'OnUserTokenInvalid',
} as const;

interface WorkspaceOpenIMRuntimeDependencies {
  clientFactory: () => Promise<WorkspaceOpenIMClient>;
  getToken: () => Promise<IMyStaffIMTokenReply>;
  handleInvite: (
    input: ICourseEvalInviteActionInput,
  ) => Promise<(Partial<IHTTPResponse> & { message?: unknown }) | undefined>;
}

interface WorkspaceIMUserProfile {
  avatar?: string;
  name?: string;
  userID: string;
}

type WorkspaceIMRuntimeErrorSource =
  | 'connection'
  | 'conversation-list'
  | 'history-latest'
  | 'history-previous'
  | 'mark-read';

const DEFAULT_INVITE_ACTION_ERROR = '操作失败，请稍后重试';

/** readInviteActionErrorMessage 只提取评课邀请接口面向用户的业务拒绝原因。 */
function readInviteActionErrorMessage(
  response?: Partial<IHTTPResponse> & { message?: unknown },
): string {
  // Web HTTP 会把后端 message 归一为 msg；兼容保留 message 是为了覆盖直连测试和历史响应。
  const rawMessage = response?.message ?? response?.msg;
  const message = typeof rawMessage === 'string' ? rawMessage.trim() : '';
  return message || DEFAULT_INVITE_ACTION_ERROR;
}

/** getStoredIMToken 通过 Entity 获取当前员工 token，并拒绝不完整的成功响应。 */
async function getStoredIMToken(): Promise<IMyStaffIMTokenReply> {
  const response = await imServiceEntity.getTokenStore().fetch();
  if (!isSuccess(response) || !response.data) throw new Error('im-token-failed');
  return response.data;
}

/** handleStoredInvite 以消息 ID 隔离动作 Store，避免多卡片并行时参数串写。 */
async function handleStoredInvite(
  input: ICourseEvalInviteActionInput,
): Promise<(Partial<IHTTPResponse> & { message?: unknown }) | undefined> {
  const store = imServiceEntity.getHandleInviteStore(input.messageID);
  store.setValues(input, 'overwrite');
  return await store.fetch();
}

const defaultDependencies: WorkspaceOpenIMRuntimeDependencies = {
  clientFactory: createWorkspaceOpenIMClient,
  getToken: getStoredIMToken,
  handleInvite: handleStoredInvite,
};

/** normalizeLiveGroupID 保留课次 conversationID 原值作为 SDK groupID，对齐旧站入群和发送语义。 */
function normalizeLiveGroupID(groupID: string): string {
  return String(groupID || '').trim();
}

/** getLiveConversationID 根据 OpenIM 约定把直播群 ID 映射成群会话 ID，已带 sg_ 时保持原样。 */
function getLiveConversationID(groupID: string): string {
  const normalizedGroupID = normalizeLiveGroupID(groupID);
  if (!normalizedGroupID) return '';
  return normalizedGroupID.startsWith('sg_') ? normalizedGroupID : `sg_${normalizedGroupID}`;
}

/** normalizeUserProfiles 把 SDK 公共资料裁剪为消息展示所需的昵称和头像。 */
function normalizeUserProfiles(value: unknown): WorkspaceIMUserProfile[] {
  return asArray(value).flatMap((item) => {
    if (!isRecord(item)) return [];
    const raw: RawOpenIMUserProfile = item;
    const userID = readString(raw.userID);
    if (!userID) return [];
    return [{
      avatar: readString(raw.faceURL),
      name: readString(raw.nickname),
      userID,
    }];
  });
}

/** WorkspaceOpenIMRuntime 在 Host 内持有单一 SDK 连接，并向 Remote 暴露稳定的最小 Bridge。 */
export class WorkspaceOpenIMRuntime {
  readonly bridge: IWorkspaceIMBridge;

  private client?: WorkspaceOpenIMClient;
  private connected = false;
  private disposed = false;
  private errorSource?: WorkspaceIMRuntimeErrorSource;
  private generation = 0;
  private historyEndByConversation = new Map<string, boolean>();
  private historyLoadedConversations = new Set<string>();
  private imUserID = '';
  private latestHistoryPromises = new Map<string, Promise<void>>();
  private listeners: Array<{ event: string; handler: OpenIMEventHandler }> = [];
  private markReadPromises = new Map<string, Promise<void>>();
  private pendingLatestHistoryRefreshes = new Set<string>();
  private profileCache = new Map<string, WorkspaceIMUserProfile>();
  private profilePromises = new Map<string, Promise<void>>();
  private startPromise?: Promise<void>;
  private synced = false;
  private readonly store = new WorkspaceIMStore();

  /** constructor 固化当前租户员工身份，运行期不允许原地切换用户。 */
  constructor(
    private readonly config: WorkspaceOpenIMConfig,
    private readonly dependencies: WorkspaceOpenIMRuntimeDependencies = defaultDependencies,
  ) {
    this.bridge = {
      ensureGroupConversation: this.ensureGroupConversation,
      getSnapshot: this.store.getSnapshot,
      handleCourseEvalInvite: this.handleCourseEvalInvite,
      loadPreviousMessages: this.loadPreviousMessages,
      markConversationRead: this.markConversationRead,
      retry: this.retry,
      sendGroupTextMessage: this.sendGroupTextMessage,
      selectConversation: this.selectConversation,
      subscribe: this.store.subscribe,
    };
  }

  /** start 以单飞方式完成鉴权、事件绑定和 SDK 登录，重复挂载不会产生双连接。 */
  start = async (): Promise<void> => {
    if (this.startPromise) return await this.startPromise;
    // 非 idle 状态已有生命周期或需要显式 retry，不能对进程级 SDK 重复 login。
    if (this.store.getSnapshot().status !== 'idle') return;
    const generation = this.generation + 1;
    this.generation = generation;
    this.disposed = false;
    this.connected = false;
    this.synced = false;
    this.errorSource = undefined;
    this.store.patch({ error: undefined, status: 'authenticating', totalUnread: undefined });
    this.startPromise = this.startGeneration(generation).finally(() => {
      this.startPromise = undefined;
    });
    return await this.startPromise;
  };

  /** dispose 先让旧异步世代失效，再解绑事件并清空全部租户内存事实。 */
  dispose = async (): Promise<void> => {
    this.generation += 1;
    this.disposed = true;
    this.store.patch({ status: 'disposing', totalUnread: undefined });
    this.unbindEvents();
    const { client } = this;
    this.client = undefined;
    this.connected = false;
    this.synced = false;
    this.errorSource = undefined;
    this.historyEndByConversation.clear();
    this.historyLoadedConversations.clear();
    this.latestHistoryPromises.clear();
    this.markReadPromises.clear();
    this.pendingLatestHistoryRefreshes.clear();
    this.profileCache.clear();
    this.profilePromises.clear();
    this.imUserID = '';
    imServiceEntity.disposeTokenStore();
    if (client) {
      try {
        await client.logout();
      } catch {
        // 销毁阶段即使网络不可用也必须继续清空本地租户事实。
      }
    }
    this.store.reset();
  };

  /** retry 从可恢复错误态重新建立当前员工连接。 */
  private retry = async (): Promise<void> => {
    if (this.store.getSnapshot().status === 'kicked') return;
    await this.dispose();
    await this.start();
  };

  /** startGeneration 执行一次不可跨租户写入的 SDK 登录世代。 */
  private async startGeneration(generation: number): Promise<void> {
    try {
      const client = await this.dependencies.clientFactory();
      if (!this.isCurrent(generation)) return;
      this.client = client;
      this.bindEvents(client, generation);
      const auth = await this.dependencies.getToken();
      if (!this.isCurrent(generation)) return;
      if (!auth.uid || !auth.imToken) throw new Error('invalid-im-auth');
      this.imUserID = auth.uid;
      this.store.patch({ status: 'connecting' });
      await client.login({
        apiAddr: this.config.apiAddr,
        token: auth.imToken,
        userID: auth.uid,
        wsAddr: this.config.wsAddr,
      });
      // login Promise 只代表调用成功；ready 必须由连接与同步两个事件共同驱动。
      if (!this.isCurrent(generation)) return;
    } catch {
      if (!this.isCurrent(generation)) return;
      this.setRuntimeError('connection', '消息服务连接失败，请稍后重试', {
        status: 'error',
        totalUnread: undefined,
      });
    }
  }

  /** bindEvents 在 login 前绑定全量生命周期事件，避免快速连接时漏事件。 */
  private bindEvents(client: WorkspaceOpenIMClient, generation: number): void {
    this.unbindEvents();
    /** bind 为当前连接世代统一增加失效保护并记录可精确解绑的监听器。 */
    const bind = (event: string, handler: OpenIMEventHandler) => {
      /** guarded 丢弃租户或账号切换后才到达的旧 SDK 事件。 */
      const guarded: OpenIMEventHandler = (payload) => {
        if (this.isCurrent(generation)) handler(payload);
      };
      client.on(event, guarded);
      this.listeners.push({ event, handler: guarded });
    };
    bind(EVENT_NAMES.connecting, () => {
      this.store.patch({ status: this.connected ? 'reconnecting' : 'connecting' });
    });
    bind(EVENT_NAMES.connectSuccess, () => {
      this.connected = true;
      this.store.patch({
        ...this.clearRuntimeError('connection'),
        status: this.synced ? 'ready' : 'syncing',
      });
      void this.finishReady(generation);
    });
    bind(EVENT_NAMES.syncStart, () => {
      this.store.patch({ status: 'syncing' });
    });
    bind(EVENT_NAMES.syncFinish, () => {
      this.synced = true;
      void this.finishReady(generation);
    });
    bind(EVENT_NAMES.connectFailed, () => {
      this.setRuntimeError('connection', '消息服务连接失败，请稍后重试', {
        status: 'error',
        totalUnread: undefined,
      });
    });
    bind(EVENT_NAMES.syncFailed, () => {
      this.setRuntimeError('connection', '消息同步失败，请稍后重试', {
        status: 'error',
        totalUnread: undefined,
      });
    });
    /** refresh 复用服务端会话顺序，并在事件到达时刷新当前消息窗口。 */
    const refresh = () => {
      void this.refreshConversations(generation, true);
    };
    bind(EVENT_NAMES.conversationChanged, refresh);
    bind(EVENT_NAMES.newConversation, refresh);
    bind(EVENT_NAMES.receiveMessages, refresh);
    // SDK 总未读包含群聊等非本批会话，因此只把事件当刷新信号，再按单聊会话重新聚合。
    bind(EVENT_NAMES.totalUnreadChanged, refresh);
    bind(EVENT_NAMES.tokenExpired, () => this.expireSession(generation));
    bind(EVENT_NAMES.tokenInvalid, () => this.expireSession(generation));
    bind(EVENT_NAMES.kickedOffline, () => {
      this.setRuntimeError('connection', '消息账号已在其他位置登录', {
        status: 'kicked',
        totalUnread: undefined,
      });
    });
  }

  /** unbindEvents 精确移除当前 Runtime 注册的回调，不影响 SDK 的其他宿主。 */
  private unbindEvents(): void {
    if (!this.client) {
      this.listeners = [];
      return;
    }
    this.listeners.forEach(({ event, handler }) => this.client?.off(event, handler));
    this.listeners = [];
  }

  /** finishReady 仅在连接和首次同步都完成后拉取会话并进入 ready。 */
  private async finishReady(generation: number): Promise<void> {
    if (!this.connected || !this.synced || !this.isCurrent(generation)) return;
    await this.refreshConversations(generation, true);
    if (this.isCurrent(generation)) this.store.patch({ status: 'ready' });
  }

  /** expireSession 使旧 token 世代失效，并用当前租户身份重新鉴权一次。 */
  private expireSession(generation: number): void {
    if (!this.isCurrent(generation)) return;
    this.store.patch({ status: 'expired', totalUnread: undefined });
    void this.retry();
  }

  /** refreshConversations 维持 SDK 服务端排序，并按需刷新当前会话消息窗口。 */
  private async refreshConversations(generation: number, refreshSelected: boolean): Promise<void> {
    const { client } = this;
    if (!client) return;
    try {
      const response = await client.getConversations();
      if (!this.isCurrent(generation)) return;
      const normalizedConversations = normalizeConversations(response);
      const snapshot = this.store.getSnapshot();
      const current = snapshot.selectedConversationID;
      const preservedConversation = current
        ? snapshot.conversations.find(item => item.conversationID === current && item.conversationType === 'group')
        : undefined;
      const conversations = preservedConversation && !normalizedConversations.some(
        item => item.conversationID === preservedConversation.conversationID,
      )
        // 直播群会话可能由详情页先行创建；SDK 会话列表短时缺席时不能把右栏切回通知会话。
        ? [...normalizedConversations, preservedConversation]
        : normalizedConversations;
      const selectedConversationID = current && conversations.some(item => item.conversationID === current)
        ? current
        : conversations[0]?.conversationID;
      this.store.patch({
        ...this.clearRuntimeError('conversation-list'),
        conversations,
        selectedConversationID,
        // 工作台角标仍只统计通知单聊；直播群聊进入详情页使用，不能放大全局未读。
        totalUnread: conversations
          .filter(item => item.conversationType !== 'group')
          .reduce((sum, item) => sum + item.unreadCount, 0),
      });
      if (refreshSelected && selectedConversationID) {
        await this.loadLatestMessages(selectedConversationID, generation);
      }
    } catch {
      if (!this.isCurrent(generation)) return;
      this.setRuntimeError('conversation-list', '会话列表加载失败，请重试');
    }
  }

  /** selectConversation 切换当前会话，并让旧会话晚到的历史响应无法覆盖新会话。 */
  private selectConversation = async (conversationID: string): Promise<void> => {
    if (!conversationID || !this.store.getSnapshot().conversations.some(
      item => item.conversationID === conversationID,
    )) return;
    this.store.patch({
      ...this.clearRuntimeError('history-latest', 'history-previous'),
      hasPreviousMessages: false,
      historyLoading: true,
      messages: [],
      selectedConversationID: conversationID,
    });
    await this.loadLatestMessages(conversationID, this.generation);
  };

  /** ensureGroupConversation 加入并选中直播群聊，兼容旧站课次 conversationID 只给 groupID 的情况。 */
  private ensureGroupConversation = async (groupID: string): Promise<string> => {
    const normalizedGroupID = normalizeLiveGroupID(groupID);
    if (!normalizedGroupID) return '';
    await this.ensureReadyForGroupBridge();
    if (!this.client) return '';
    const conversationID = getLiveConversationID(normalizedGroupID);
    await this.ensureJoinedGroup(normalizedGroupID);
    await this.refreshConversations(this.generation, false);
    const snapshot = this.store.getSnapshot();
    if (!snapshot.conversations.some(item => item.conversationID === conversationID)) {
      // 新群加入后 SDK 会话列表可能延迟刷新；先补一个最小会话壳，让详情页能立即拉历史。
      this.store.patch({
        conversations: [
          ...snapshot.conversations,
          {
            conversationID,
            conversationType: 'group',
            latestMessage: '',
            latestMessageTime: 0,
            name: '直播交流',
            unreadCount: 0,
            userID: normalizedGroupID,
          },
        ],
      });
    }
    if (this.store.getSnapshot().selectedConversationID !== conversationID) {
      // 发送前也会复用本方法；同会话重复 select 会清空现有消息并造成交流区闪烁。
      await this.selectConversation(conversationID);
    } else if (!this.historyLoadedConversations.has(conversationID)) {
      // 同会话上次历史失败时保留重试入口，但已成功加载后发送不能重复清空或拉取。
      this.store.patch({ historyLoading: true });
      await this.loadLatestMessages(conversationID, this.generation);
    }
    return conversationID;
  };

  /** sendGroupTextMessage 通过 Runtime 统一发送直播群文本，并用返回消息即时补齐本地列表。 */
  private sendGroupTextMessage = async (groupID: string, content: string): Promise<void> => {
    const text = content.trim();
    const normalizedGroupID = normalizeLiveGroupID(groupID);
    if (!text || !normalizedGroupID) {
      throw new Error('group-message-unavailable');
    }
    const conversationID = await this.ensureGroupConversation(normalizedGroupID);
    if (!conversationID || !this.client?.createTextMessage || !this.client.sendMessage) {
      throw new Error('group-message-unavailable');
    }
    const draft = await this.client.createTextMessage(text);
    const response = await this.client.sendMessage({
      groupID: normalizedGroupID,
      message: draft,
      recvID: '',
    });
    const message = normalizeMessage(response, conversationID, this.imUserID);
    if (message && this.store.getSnapshot().selectedConversationID === conversationID) {
      this.store.patch({
        messages: mergeMessages(
          this.store.getSnapshot().messages,
          this.applyCachedProfiles([message]),
        ),
      });
      void this.hydrateMessageProfiles(conversationID, this.generation, [message]);
    }
    await this.loadLatestMessages(conversationID, this.generation);
  };

  /** ensureReadyForGroupBridge 等待 Host IM 完成连接与首次同步，避免直播页首帧早于 SDK ready 时空跑。 */
  private async ensureReadyForGroupBridge(): Promise<void> {
    const { status } = this.store.getSnapshot();
    if (status === 'ready') return;
    if (status === 'idle') await this.start();
    if (this.store.getSnapshot().status === 'ready') return;
    await new Promise<void>((resolve, reject) => {
      let unsubscribe: () => void = () => undefined;
      /** finish 统一解绑 ready 等待监听，避免成功和失败竞态下留下重复订阅。 */
      const finish = (callback: () => void) => {
        unsubscribe();
        callback();
      };
      unsubscribe = this.store.subscribe(() => {
        const nextStatus = this.store.getSnapshot().status;
        if (nextStatus === 'ready') {
          finish(resolve);
          return;
        }
        if (['disposing', 'error', 'expired', 'kicked'].includes(nextStatus)) {
          finish(() => reject(new Error(`openim-${nextStatus}`)));
        }
      });
      const latestStatus = this.store.getSnapshot().status;
      if (latestStatus === 'ready') finish(resolve);
      if (['disposing', 'error', 'expired', 'kicked'].includes(latestStatus)) {
        finish(() => reject(new Error(`openim-${latestStatus}`)));
      }
    });
  }

  /** ensureJoinedGroup 用 SDK 已加入状态保护 joinGroup，避免每次打开直播都重复申请入群。 */
  private async ensureJoinedGroup(groupID: string): Promise<void> {
    if (!this.client?.joinGroup) return;
    if (this.client.isJoinGroup) {
      try {
        const result = await this.client.isJoinGroup(groupID);
        const data = (result as { data?: unknown })?.data ?? result;
        if (data === true || data === 1) return;
      } catch {
        // 旧站也是以 joinGroup 作为最终兜底；查询失败时继续尝试加入。
      }
    }
    try {
      await this.client.joinGroup({ groupID, joinSource: 3, reqMsg: '' });
    } catch {
      // 已入群、重复申请或服务端异步建群都不应阻断历史消息读取和发送时的明确错误。
    }
  }

  /** loadLatestMessages 首拉最新消息窗口，并丢弃切换会话后的旧响应。 */
  private async loadLatestMessages(conversationID: string, generation: number): Promise<void> {
    const existing = this.latestHistoryPromises.get(conversationID);
    if (existing) {
      // 新消息事件可能晚于在途请求的服务端快照，合并等待后仍需补跑一次最新窗口。
      this.pendingLatestHistoryRefreshes.add(conversationID);
      return await existing;
    }
    const request = this.commitLatestMessages(conversationID, generation).finally(async () => {
      if (this.latestHistoryPromises.get(conversationID) === request) {
        this.latestHistoryPromises.delete(conversationID);
      }
      const shouldRefreshAgain = this.pendingLatestHistoryRefreshes.delete(conversationID);
      if (shouldRefreshAgain && this.isSelected(generation, conversationID)) {
        await this.loadLatestMessages(conversationID, generation);
      }
    });
    this.latestHistoryPromises.set(conversationID, request);
    return await request;
  }

  /** commitLatestMessages 执行单次最新历史请求，并记录该会话是否已有可复用的成功窗口。 */
  private async commitLatestMessages(conversationID: string, generation: number): Promise<void> {
    const { client } = this;
    if (!client) return;
    try {
      const response = await client.getHistory(conversationID, '');
      if (!this.isSelected(generation, conversationID)) return;
      const messages = this.applyCachedProfiles(
        normalizeMessages(response, conversationID, this.imUserID),
      );
      const isEnd = readHistoryEnd(response);
      this.historyEndByConversation.set(conversationID, isEnd);
      this.historyLoadedConversations.add(conversationID);
      this.store.patch({
        ...this.clearRuntimeError('history-latest'),
        hasPreviousMessages: !isEnd,
        historyLoading: false,
        messages: mergeMessages(this.store.getSnapshot().messages, messages),
      });
      void this.hydrateMessageProfiles(conversationID, generation, messages);
    } catch {
      if (!this.isSelected(generation, conversationID)) return;
      this.historyLoadedConversations.delete(conversationID);
      this.setRuntimeError('history-latest', '消息加载失败，请重试', {
        historyLoading: false,
      });
    }
  }

  /** loadPreviousMessages 分页加载更早消息，并保留页面负责的视觉滚动位置。 */
  private loadPreviousMessages = async (conversationID: string): Promise<void> => {
    const snapshot = this.store.getSnapshot();
    if (
      snapshot.historyLoading
      || snapshot.selectedConversationID !== conversationID
      || this.historyEndByConversation.get(conversationID)
    ) return;
    const first = snapshot.messages[0];
    if (!first?.clientMsgID || !this.client) {
      this.historyEndByConversation.set(conversationID, true);
      this.store.patch({ hasPreviousMessages: false });
      return;
    }
    const { generation } = this;
    this.store.patch({ historyLoading: true });
    try {
      const response = await this.client.getHistory(conversationID, first.clientMsgID);
      if (!this.isSelected(generation, conversationID)) return;
      const messages = this.applyCachedProfiles(
        normalizeMessages(response, conversationID, this.imUserID),
      );
      const isEnd = readHistoryEnd(response);
      this.historyEndByConversation.set(conversationID, isEnd);
      this.store.patch({
        ...this.clearRuntimeError('history-previous'),
        hasPreviousMessages: !isEnd,
        historyLoading: false,
        messages: mergeMessages(messages, snapshot.messages),
      });
      void this.hydrateMessageProfiles(conversationID, generation, messages);
    } catch {
      if (this.isSelected(generation, conversationID)) {
        this.setRuntimeError('history-previous', '更早消息加载失败，请重试', {
          historyLoading: false,
        });
      }
    }
  };

  /** applyCachedProfiles 用已查询资料覆盖消息快照中的昵称和头像，保证资料更新后展示一致。 */
  private applyCachedProfiles(messages: readonly WorkspaceIMMessage[]): WorkspaceIMMessage[] {
    return messages.map((message) => {
      const profile = this.profileCache.get(message.sender);
      if (!profile) return message;
      const senderAvatar = message.senderAvatar || profile.avatar;
      const senderName = message.senderName || profile.name;
      if (message.senderName === senderName && message.senderAvatar === senderAvatar) {
        return message;
      }
      return {
        ...message,
        senderAvatar,
        senderName,
      };
    });
  }

  /** hydrateMessageProfiles 批量补齐当前消息窗口的发送者资料，失败时保留消息原始展示。 */
  private async hydrateMessageProfiles(
    conversationID: string,
    generation: number,
    messages: readonly WorkspaceIMMessage[],
  ): Promise<void> {
    const userIDs = Array.from(new Set(messages.map(message => message.sender).filter(Boolean)));
    if (!userIDs.length) return;
    await this.ensureUserProfiles(userIDs, generation);
    if (!this.isSelected(generation, conversationID)) return;
    const snapshot = this.store.getSnapshot();
    const hydratedMessages = this.applyCachedProfiles(snapshot.messages);
    if (hydratedMessages.every((message, index) => message === snapshot.messages[index])) return;
    this.store.patch({ messages: hydratedMessages });
  }

  /** ensureUserProfiles 对缺失资料执行单飞批量查询，并阻止旧租户响应写回当前 Runtime。 */
  private async ensureUserProfiles(userIDs: readonly string[], generation: number): Promise<void> {
    const pending = new Set<Promise<void>>();
    const missing: string[] = [];
    Array.from(new Set(userIDs)).forEach((userID) => {
      if (this.profileCache.has(userID)) return;
      const existing = this.profilePromises.get(userID);
      if (existing) {
        pending.add(existing);
        return;
      }
      missing.push(userID);
    });
    const { client } = this;
    if (missing.length && client) {
      const request = client.getUsersInfo(missing)
        .then((response) => {
          if (!this.isCurrent(generation)) return;
          const profiles = normalizeUserProfiles(response);
          profiles.forEach((profile) => {
            this.profileCache.set(profile.userID, profile);
          });
          // 成功响应未返回的系统或已删除用户也做负缓存，避免每次消息事件重复放大请求。
          missing.forEach((userID) => {
            if (!this.profileCache.has(userID)) this.profileCache.set(userID, { userID });
          });
        })
        .catch(() => {
          // 用户资料是消息的增强信息，查询失败不能阻断历史消息读取和发送。
        })
        .finally(() => {
          missing.forEach((userID) => {
            if (this.profilePromises.get(userID) === request) {
              this.profilePromises.delete(userID);
            }
          });
        });
      missing.forEach(userID => this.profilePromises.set(userID, request));
      pending.add(request);
    }
    await Promise.all(pending);
  }

  /** markConversationRead 合并同会话的并发确认，避免滚动和按钮同时触发重复 SDK 请求。 */
  private markConversationRead = async (conversationID: string): Promise<void> => {
    const existing = this.markReadPromises.get(conversationID);
    if (existing) return await existing;
    const snapshot = this.store.getSnapshot();
    const conversation = snapshot.conversations.find(item => item.conversationID === conversationID);
    if (!conversation?.unreadCount || !this.client) return;
    const { generation } = this;
    const pending = this.commitConversationRead(conversationID, generation).finally(() => {
      if (this.markReadPromises.get(conversationID) === pending) {
        this.markReadPromises.delete(conversationID);
      }
    });
    this.markReadPromises.set(conversationID, pending);
    return await pending;
  };

  /** commitConversationRead 在 SDK 成功后基于最新快照清零，避免覆盖并发到达的其他会话变化。 */
  private async commitConversationRead(
    conversationID: string,
    generation: number,
  ): Promise<void> {
    try {
      await this.client?.markConversationRead(conversationID);
    } catch {
      if (this.isCurrent(generation)) {
        // 已读失败时保留服务端未读事实和右下角入口，让用户可以再次确认。
        this.setRuntimeError('mark-read', '消息已读状态更新失败，请重试');
      }
      return;
    }
    if (!this.isCurrent(generation)) return;
    const latest = this.store.getSnapshot();
    const conversations = latest.conversations.map(item => (
      item.conversationID === conversationID ? { ...item, unreadCount: 0 } : item
    ));
    this.store.patch({
      ...this.clearRuntimeError('mark-read'),
      conversations,
      totalUnread: conversations
        .filter(item => item.conversationType !== 'group')
        .reduce((sum, item) => sum + item.unreadCount, 0),
    });
  }

  /** handleCourseEvalInvite 按消息维度阻止重复提交，并保留失败后的重试上下文。 */
  private handleCourseEvalInvite = async (input: ICourseEvalInviteActionInput): Promise<void> => {
    const actionKey = input.messageID;
    if (!actionKey || this.store.getSnapshot().actionStatus[actionKey] === 'pending') return;
    const actionError = { ...this.store.getSnapshot().actionError };
    // 重试开始时只清掉当前卡片的旧错误，不能影响其他并行邀请的失败原因。
    delete actionError[actionKey];
    this.store.patch({
      actionError,
      actionStatus: { ...this.store.getSnapshot().actionStatus, [actionKey]: 'pending' },
    });
    let failureMessage = DEFAULT_INVITE_ACTION_ERROR;
    try {
      const response = await this.dependencies.handleInvite(input);
      if (!isSuccess(response)) {
        failureMessage = readInviteActionErrorMessage(response);
        throw new Error('invite-action-failed');
      }
      this.store.patch({
        actionStatus: { ...this.store.getSnapshot().actionStatus, [actionKey]: 'succeeded' },
      });
      imServiceEntity.disposeHandleInviteStore(actionKey);
    } catch {
      this.store.patch({
        actionError: {
          ...this.store.getSnapshot().actionError,
          [actionKey]: failureMessage,
        },
        actionStatus: { ...this.store.getSnapshot().actionStatus, [actionKey]: 'error' },
      });
      throw new Error('invite-action-failed');
    }
  };

  /** setRuntimeError 记录错误归属，使其他并发成功操作不能误清当前失败提示。 */
  private setRuntimeError(
    source: WorkspaceIMRuntimeErrorSource,
    error: string,
    values: Partial<WorkspaceIMSnapshot> = {},
  ): void {
    this.errorSource = source;
    this.store.patch({ ...values, error });
  }

  /** clearRuntimeError 只清理由指定操作产生的当前错误。 */
  private clearRuntimeError(
    ...sources: WorkspaceIMRuntimeErrorSource[]
  ): Partial<WorkspaceIMSnapshot> {
    if (!this.errorSource || !sources.includes(this.errorSource)) return {};
    this.errorSource = undefined;
    return { error: undefined };
  }

  /** isCurrent 判断异步结果是否仍属于当前未销毁租户世代。 */
  private isCurrent(generation: number): boolean {
    return !this.disposed && generation === this.generation;
  }

  /** isSelected 同时校验租户世代和会话身份，避免切换会话时串写消息。 */
  private isSelected(generation: number, conversationID: string): boolean {
    return this.isCurrent(generation)
      && this.store.getSnapshot().selectedConversationID === conversationID;
  }
}
